/**
 * RequireEmailConfirmed - HOC that checks if user has confirmed their email
 * Shows a confirmation prompt if email is not verified
 */

import { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { EmailConfirmationPrompt } from './EmailConfirmationPrompt';

interface RequireEmailConfirmedProps {
  children: ReactNode;
  /** Custom fallback component if email not confirmed */
  fallback?: ReactNode;
}

export function RequireEmailConfirmed({ children, fallback }: RequireEmailConfirmedProps) {
  const { user, emailConfirmed, isLoading } = useAuth();

  // Still loading auth state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - ProtectedRoute should handle this, but just in case
  if (!user) {
    return null;
  }

  // Email not confirmed - show prompt or fallback
  if (!emailConfirmed) {
    return <>{fallback ?? <EmailConfirmationPrompt />}</>;
  }

  // Email confirmed - render children
  return <>{children}</>;
}

export default RequireEmailConfirmed;
